import { useState } from 'react';
import { TerminalSquare } from 'lucide-react';

import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';
import { maskApiKey } from 'app/utils';
import ApiKey from 'components/FormRequestBlock/ApiKey';
import CheckoutIntentApiKey from 'components/FormRequestBlock/CheckoutIntentApiKey';
import JWTValidationKey from 'components/FormRequestBlock/JWTValidationKey';
import WebhooksUrl from 'components/FormRequestBlock/WebhooksUrl';
import { CodeBlock } from 'pages/Dashboard/components/CodeBlock';
import { Typography } from 'components/ui/typography';
import { cn } from 'lib/utils';

import { ReactComponent as TypescriptIcon } from 'assets/icons/languages/typescript.svg';
import { ReactComponent as PythonIcon } from 'assets/icons/languages/python.svg';
import { ReactComponent as JavaIcon } from 'assets/icons/languages/java.svg';
import { ReactComponent as RubyIcon } from 'assets/icons/languages/ruby.svg';

import { isLegacyBilling } from 'lib/utils';
import { canRead, canWrite } from 'app/access';

import {
  createTypescriptInitSnippet,
  createPythonInitSnippet,
  createJavaInitSnippet,
  createRubyInitSnippet,
  createCurlInitSnippet,
} from './initSnippets';

import 'pages/Account/style.scss';

const sectionHeaderCn = 'mb-5 mt-16 first-of-type:mt-0';

type Language = 'typescript' | 'python' | 'java' | 'ruby' | 'curl';

const languageConfig: Record<
  Language,
  {
    label: string;
    icon: React.ReactNode;
    highlighter: string;
    snippet: (apiKey: string) => string;
  }
> = {
  typescript: {
    label: 'TypeScript',
    icon: <TypescriptIcon className="size-4" />,
    highlighter: 'typescript',
    snippet: createTypescriptInitSnippet,
  },
  python: {
    label: 'Python',
    icon: <PythonIcon className="size-4" />,
    highlighter: 'python',
    snippet: createPythonInitSnippet,
  },
  java: {
    label: 'Kotlin',
    icon: <JavaIcon className="size-4" />,
    highlighter: 'kotlin',
    snippet: createJavaInitSnippet,
  },
  ruby: {
    label: 'Ruby',
    icon: <RubyIcon className="size-4" />,
    highlighter: 'ruby',
    snippet: createRubyInitSnippet,
  },
  curl: {
    label: 'cURL',
    icon: <TerminalSquare className="size-4" />,
    highlighter: 'bash',
    snippet: createCurlInitSnippet,
  },
};

const Page = () => {
  const userInfo = useAppSelector(selectUserInfo);
  const { billingPeriodSettings, apiKey = '' } = userInfo || {};
  const isLegacy = isLegacyBilling(billingPeriodSettings);
  const canManageCheckoutIntentKeys = canWrite(userInfo, 'checkout_intents');
  const canViewWebhookEndpoint = canRead(userInfo, 'webhook_endpoints');
  const [language, setLanguage] = useState<Language>('typescript');

  const current = languageConfig[language];

  // SDK init lives as an attached footer on the API-key card — flush so the
  // code block fills the footer edge-to-edge, sharing the surface-2 tint.
  // Language picker is a segmented button group: each button claims an equal
  // slice of the row via `flex-1`, with the active option carrying the lime
  // accent so the choice is unambiguous.
  const languagePicker = (
    <div className="flex w-full">
      {(Object.keys(languageConfig) as Language[]).map((lang) => {
        const isActive = lang === language;
        return (
          <button
            key={lang}
            type="button"
            onClick={() => setLanguage(lang)}
            className={cn(
              'flex flex-1 items-center justify-center gap-2 border-r border-[var(--line)] px-3 py-2 text-xs transition-colors last:border-r-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
              isActive
                ? 'bg-[var(--accent)] text-[var(--accent-fg)]'
                : 'text-[var(--text-muted)] hover:bg-[var(--surface)] hover:text-[var(--text)]',
            )}
          >
            {languageConfig[lang].icon}
            {languageConfig[lang].label}
          </button>
        );
      })}
    </div>
  );

  const sdkFooter = (
    <CodeBlock
      code={current.snippet(apiKey)}
      mask={maskApiKey}
      language={current.highlighter}
      headerLeft={languagePicker}
      flush
    />
  );

  return (
    <div className="account-container">
      {/* For non-legacy accounts, the page is single-purpose — the API Keys
          route label already names the section, so a redundant h2 above the
          one card just adds chrome without information. Webhooks slot in
          directly below since they share the Checkout-Intent integration.
          Legacy accounts surface multiple distinct auth schemes, so each
          gets its own labeled section. */}
      {!isLegacy ? (
        <>
          {canManageCheckoutIntentKeys && <CheckoutIntentApiKey footer={sdkFooter} flushFooter />}
          {canViewWebhookEndpoint && (
            <div className="mt-6">
              <WebhooksUrl />
            </div>
          )}
        </>
      ) : (
        <>
          {canManageCheckoutIntentKeys && (
            <>
              <Typography variant="h2" as="h2" className={sectionHeaderCn}>
                Checkout Intent API Authentication
              </Typography>
              <CheckoutIntentApiKey footer={sdkFooter} flushFooter />
            </>
          )}

          <Typography variant="h2" as="h2" className={sectionHeaderCn}>
            Sync GraphQL API Authentication
          </Typography>
          <ApiKey />

          <Typography variant="h2" as="h2" className={sectionHeaderCn}>
            JWT Authentication
          </Typography>
          <JWTValidationKey />

          {canViewWebhookEndpoint && (
            <>
              <Typography variant="h2" as="h2" className={sectionHeaderCn}>
                Webhooks
              </Typography>
              <WebhooksUrl />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Page;
